import { useState, useEffect, useCallback, useRef } from 'react';
import { LeadService } from '../services';
import type { CheckoutLead, CommercialLeadFilters, PaginationInfo } from '../types';

const SEARCH_DEBOUNCE_MS = 400;

export type LeadFilters = CommercialLeadFilters;

function areArraysEqual<T>(left: T[] | undefined, right: T[] | undefined): boolean {
  const leftValues = left ?? [];
  const rightValues = right ?? [];

  if (leftValues.length !== rightValues.length) {
    return false;
  }

  return leftValues.every((value, index) => value === rightValues[index]);
}

function areLeadFiltersEqualExceptSearch(left: LeadFilters, right: LeadFilters): boolean {
  return areArraysEqual(left.classifications, right.classifications)
    && areArraysEqual(left.leadTemperatureTypes, right.leadTemperatureTypes)
    && areArraysEqual(left.personaTypes, right.personaTypes)
    && left.hasPhotos === right.hasPhotos
    && left.hasParq === right.hasParq
    && left.hasAnamnesis === right.hasAnamnesis
    && left.hasPaid === right.hasPaid
    && areArraysEqual(left.paymentMethods, right.paymentMethods)
    && areArraysEqual(left.cancellationReasons, right.cancellationReasons)
    && areArraysEqual(left.investmentInterests, right.investmentInterests)
    && areArraysEqual(left.startTimeExpectations, right.startTimeExpectations)
    && areArraysEqual(left.mainGoals, right.mainGoals)
    && areArraysEqual(left.commercialStages, right.commercialStages)
    && areArraysEqual(left.projectCodes, right.projectCodes)
    && areArraysEqual(left.commercialOwnerIds, right.commercialOwnerIds)
    && left.joinedAtStart === right.joinedAtStart
    && left.joinedAtEnd === right.joinedAtEnd
    && left.declinedRescue === right.declinedRescue;
}

export interface UseLeadsReturn {
  leads: CheckoutLead[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refresh: (options?: { silent?: boolean }) => void;
  updateLead: (id: string, data: Partial<CheckoutLead>) => Promise<void>;
  patchLeadOnboardingStatus: (updates: Record<number, Partial<CheckoutLead>>) => void;
  patchLead: (
    matcher: { studentId?: number | null; qualificationId?: number | null },
    data: Partial<CheckoutLead>,
  ) => void;
}

export function useLeads(
  page: number = 1,
  pageSize: number = 10,
  filters: LeadFilters = {},
): UseLeadsReturn {
  const [leads, setLeads] = useState<CheckoutLead[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reqId = useRef(0);
  const silentReqId = useRef(0);

  const fetchPage = useCallback(
    async (
      requestedPage: number,
      requestedPageSize: number,
      serverFilters: LeadFilters,
      options?: { silent?: boolean },
    ) => {
      const silent = Boolean(options?.silent);

      // Background refresh: update data in place without the loading opacity flash.
      if (silent) {
        const snapshotReqId = reqId.current;
        const currentSilentReq = ++silentReqId.current;

        try {
          const response = await LeadService.listPaginated(
            requestedPage,
            requestedPageSize,
            serverFilters,
          );

          if (currentSilentReq !== silentReqId.current) return;
          if (snapshotReqId !== reqId.current) return;

          setLeads(response.data);
          setPagination(response.pagination);
        } catch {
          // Silent refresh failures should not interrupt the commercial screen.
        }

        return;
      }

      const currentReq = ++reqId.current;
      silentReqId.current += 1;

      try {
        setLoading(true);
        setError(null);

        const response = await LeadService.listPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentReq !== reqId.current) return;

        setLeads(response.data);
        setPagination(response.pagination);
      } catch (err: any) {
        if (currentReq !== reqId.current) return;
        setError(err?.message || 'Erro ao carregar leads');
      } finally {
        if (currentReq === reqId.current) setLoading(false);
      }
    },
    [],
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevFilters = useRef(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const currentFilters = filtersRef.current;
    const onlySearchChanged = currentFilters.searchName !== prevFilters.current.searchName
      && areLeadFiltersEqualExceptSearch(currentFilters, prevFilters.current);

    prevFilters.current = currentFilters;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.searchName,
    filters.classifications,
    filters.leadTemperatureTypes,
    filters.personaTypes,
    filters.hasPhotos,
    filters.hasParq,
    filters.hasAnamnesis,
    filters.hasPaid,
    filters.paymentMethods,
    filters.cancellationReasons,
    filters.investmentInterests,
    filters.startTimeExpectations,
    filters.mainGoals,
    filters.commercialStages,
    filters.projectCodes,
    filters.commercialOwnerIds,
    filters.joinedAtStart,
    filters.joinedAtEnd,
    filters.declinedRescue,
    fetchPage,
    page,
    pageSize,
  ]);

  const refresh = useCallback((options?: { silent?: boolean }) => {
    fetchPage(page, pageSize, filters, options);
  }, [fetchPage, filters, page, pageSize]);

  const updateLead = useCallback(async (id: string, data: Partial<CheckoutLead>) => {
    await LeadService.update(id, data);
    setLeads((prev) => prev.map((lead) => (
      String(lead.checkout_id) === id ? { ...lead, ...data } : lead
    )));
  }, []);

  const patchLeadOnboardingStatus = useCallback((updates: Record<number, Partial<CheckoutLead>>) => {
    setLeads((prev) => prev.map((lead) => {
      if (lead.student_id == null) {
        return lead;
      }

      const update = updates[lead.student_id];
      return update ? { ...lead, ...update } : lead;
    }));
  }, []);

  const patchLead = useCallback((
    matcher: { studentId?: number | null; qualificationId?: number | null },
    data: Partial<CheckoutLead>,
  ) => {
    setLeads((prev) => prev.map((lead) => {
      const matchesStudent = matcher.studentId != null
        && lead.student_id != null
        && lead.student_id === matcher.studentId;
      const matchesQualification = matcher.qualificationId != null
        && lead.qualification_id != null
        && lead.qualification_id === matcher.qualificationId;

      return matchesStudent || matchesQualification ? { ...lead, ...data } : lead;
    }));
  }, []);

  return {
    leads,
    pagination,
    loading,
    error,
    refresh,
    updateLead,
    patchLeadOnboardingStatus,
    patchLead,
  };
}
